/**
 * AdminMyMouvPanel — Admin view of all events, reports, sales, payouts.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ticket, AlertTriangle, DollarSign, CheckCircle2, Loader2, Users, Download, TrendingUp, Trash2, History } from 'lucide-react';
import AdminReportsAuditPanel from './AdminReportsAuditPanel';
import { getMediaUrl } from '../utils/mediaUrl';

const AdminMyMouvPanel = () => {
  const [tab, setTab] = useState('events'); // events | past | reports | stats
  const [events, setEvents] = useState([]);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutBusy, setPayoutBusy] = useState(null);
  const [auditReportId, setAuditReportId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'reports') {
        const r = await axios.get('/api/admin/mymouv/reports?status=pending');
        setReports(r.data?.reports || []);
      } else if (tab === 'stats') {
        const r = await axios.get('/api/admin/mymouv/stats');
        setStats(r.data);
      } else {
        const r = await axios.get(`/api/admin/mymouv/events?past=${tab === 'past'}`);
        setEvents(r.data?.events || []);
      }
    } catch (_e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const validatePayout = async (eventId) => {
    setPayoutBusy(eventId);
    try {
      await axios.post(`/api/admin/mymouv/events/${eventId}/payout`, { validated: true });
      toast.success('Versement validé. Email envoyé à l\'organisateur.');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setPayoutBusy(null); }
  };

  const resolveReport = async (reportId) => {
    try {
      await axios.post(`/api/admin/mymouv/reports/${reportId}/resolve`);
      toast.success('Signalement traité');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const removeEvent = async (eventId, title, ticketsSold) => {
    const msg = ticketsSold > 0
      ? `⚠️ Supprimer « ${title} » ? ${ticketsSold} billet(s) déjà vendu(s) seront ANNULÉS. L'organisateur sera notifié par email.`
      : `Supprimer « ${title} » définitivement ?`;
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`/api/admin/mymouv/events/${eventId}`);
      toast.success('Événement supprimé');
      window.dispatchEvent(new CustomEvent('mymouv:changed'));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div data-testid="admin-mymouv-panel">
      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'events', label: 'À venir', icon: Ticket },
          { id: 'past', label: 'Passés', icon: CheckCircle2 },
          { id: 'reports', label: 'Signalements', icon: AlertTriangle },
          { id: 'stats', label: 'Statistiques', icon: TrendingUp },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 ${tab === t.id ? 'bg-fuchsia-500 text-white' : 'bg-white/5 hover:bg-white/10 text-white/70'}`} data-testid={`admin-mymouv-tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
      ) : tab === 'stats' ? (
        stats ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Événements" value={stats.total_events} icon={Ticket} />
              <StatCard label="Publiés" value={stats.published_events} icon={CheckCircle2} />
              <StatCard label="Billets vendus" value={stats.total_tickets_sold} icon={Users} />
              <StatCard label="Signalements ouverts" value={stats.pending_reports} icon={AlertTriangle} highlight={stats.pending_reports > 0} />
              <StatCard label="Versements en attente" value={stats.pending_payouts} icon={DollarSign} />
            </div>
            {stats.revenue && (
              <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-400/30">
                <h4 className="text-sm font-bold mb-2 text-emerald-300">Revenu global plateforme</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-white/50 text-xs">Brut</div><div className="font-bold text-lg">{(stats.revenue.gross_cents / 100).toFixed(2)} €</div></div>
                  <div><div className="text-white/50 text-xs">Frais Stripe</div><div className="text-orange-300">{(stats.revenue.stripe_fee_cents / 100).toFixed(2)} €</div></div>
                  <div><div className="text-white/50 text-xs">Commission Destine</div><div className="font-bold text-emerald-300">{(stats.revenue.platform_fee_cents / 100).toFixed(2)} €</div></div>
                  <div><div className="text-white/50 text-xs">Reversé organisateurs</div><div className="text-cyan-300">{(stats.revenue.net_cents / 100).toFixed(2)} €</div></div>
                </div>
              </div>
            )}
            {stats.top_events?.length > 0 && (
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                <h4 className="text-sm font-bold mb-3">Top 5 événements</h4>
                <div className="space-y-2">
                  {stats.top_events.map((e, i) => (
                    <div key={e.event_id} className="flex items-center justify-between text-sm">
                      <div><span className="text-white/40 mr-2">#{i + 1}</span> {e.event?.title || e.event_id.slice(0, 8)}</div>
                      <div><span className="text-white/60 mr-3">{e.sold} billets</span><span className="text-emerald-300 font-bold">{(e.revenue_cents / 100).toFixed(2)} €</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(stats.promo_tickets > 0 || stats.promo_codes_total > 0) && (
              <div className="p-4 rounded-2xl bg-gradient-to-br from-fuchsia-500/10 to-purple-500/10 border border-fuchsia-400/30" data-testid="admin-mymouv-promo-stats">
                <h4 className="text-sm font-bold mb-2 text-fuchsia-300">Codes promo</h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><div className="text-white/50 text-xs">Codes générés</div><div className="font-bold text-lg">{stats.promo_codes_total || 0}</div></div>
                  <div><div className="text-white/50 text-xs">Codes utilisés</div><div className="font-bold text-fuchsia-300">{stats.promo_codes_used || 0}</div></div>
                  <div><div className="text-white/50 text-xs">Billets promo émis</div><div className="font-bold text-fuchsia-300">{stats.promo_tickets || 0}</div></div>
                </div>
              </div>
            )}
          </div>
        ) : <div className="text-white/40">Aucune donnée</div>
      ) : tab === 'reports' ? (
        <>
          <AdminReportsAuditPanel
            kind="mymouv"
            selectedReportId={auditReportId}
            onCloseSelected={() => setAuditReportId(null)}
          />
          {reports.length === 0 ? (
            <div className="text-center py-12 text-white/40">Aucun signalement en attente.</div>
          ) : (
            <div className="space-y-3">
              {reports.map(r => (
                <div key={r.id} className="rounded-2xl bg-red-500/5 border border-red-400/30 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-red-200">{r.event?.title || '—'}</div>
                      <div className="text-xs text-white/60 mt-1">{r.reason}</div>
                      <div className="text-sm text-white/80 mt-2 whitespace-pre-wrap">{r.details}</div>
                      <div className="text-[10px] text-white/40 mt-2">{r.created_at?.slice(0, 19).replace('T', ' ')}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button onClick={() => setAuditReportId(r.id)} className="px-3 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-semibold flex items-center gap-1" data-testid={`open-history-${r.id}`}><History className="w-3 h-3" /> Historique</button>
                      <button onClick={() => resolveReport(r.id)} className="px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-xs font-semibold" data-testid={`admin-mymouv-resolve-${r.id}`}>Marquer traité</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : events.length === 0 ? (
        <div className="text-center py-12 text-white/40">Aucun événement.</div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="rounded-2xl bg-white/5 border border-white/10 p-4" data-testid={`admin-mymouv-event-${ev.id}`}>
              <div className="flex items-start gap-4">
                {ev.cover_image_url && <img src={getMediaUrl(ev.cover_image_url)} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{ev.title}
                    {ev.sold_out && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500 text-white">COMPLET</span>}
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    {ev.event_date} · Par {ev.organizer?.name || ev.organizer?.email || '—'}
                  </div>

                  {ev.revenue && (
                    <div className="mt-3 p-3 rounded-lg bg-black/30 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <div className="text-white/50 flex items-center gap-1"><Users className="w-3 h-3" /> Vendus</div>
                        <div className="font-bold text-white">{ev.tickets_sold}</div>
                      </div>
                      <div>
                        <div className="text-white/50">Brut</div>
                        <div className="font-bold">{(ev.revenue.gross_cents / 100).toFixed(2)} €</div>
                      </div>
                      <div>
                        <div className="text-white/50">Stripe (1,5%+0,25€)</div>
                        <div className="text-orange-300">-{(ev.revenue.stripe_fee_cents / 100).toFixed(2)} €</div>
                      </div>
                      <div>
                        <div className="text-white/50">Destine (0,75€/billet)</div>
                        <div className="text-orange-300">-{(ev.revenue.platform_fee_cents / 100).toFixed(2)} €</div>
                      </div>
                      <div className="col-span-2 md:col-span-4 pt-2 border-t border-white/10 flex items-center justify-between">
                        <div>
                          <div className="text-white/50">À verser</div>
                          <div className="font-bold text-emerald-300 text-lg">{(ev.revenue.net_cents / 100).toFixed(2)} €</div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/50">IBAN</div>
                          <div className="font-mono text-xs">{ev.iban_snapshot || '—'}</div>
                          <div className="text-[10px] text-white/40">{ev.iban_holder}</div>
                        </div>
                      </div>
                      {(ev.promo_tickets > 0 || ev.promo_codes_used > 0) && (
                        <div className="col-span-2 md:col-span-4 pt-2 border-t border-white/10 flex items-center justify-between text-fuchsia-200">
                          <span className="text-xs">Codes promo utilisés</span>
                          <span className="text-xs font-bold">{ev.promo_codes_used || 0} codes · {ev.promo_tickets || 0} billets gratuits</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {ev.payout?.status === 'validated' ? (
                      <span className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> Versement validé
                      </span>
                    ) : (
                      <button onClick={() => validatePayout(ev.id)} disabled={payoutBusy === ev.id || ev.tickets_sold === 0} className="px-3 py-1.5 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-50 text-xs font-semibold flex items-center gap-1" data-testid={`admin-mymouv-payout-${ev.id}`}>
                        {payoutBusy === ev.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <DollarSign className="w-3 h-3" />}
                        Valider le versement
                      </button>
                    )}
                    {ev.tickets_sold > 0 && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/mymouv/events/${ev.id}/export.csv`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1" data-testid={`admin-mymouv-csv-${ev.id}`}>
                        <Download className="w-3.5 h-3.5" /> Export CSV
                      </a>
                    )}
                    {ev.sale_closed && (
                      <span className="px-3 py-1.5 rounded-lg bg-yellow-500/20 text-yellow-300 text-xs">Vente clôturée</span>
                    )}
                    <button onClick={() => removeEvent(ev.id, ev.title, ev.tickets_sold)} className="ml-auto px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs flex items-center gap-1" data-testid={`admin-mymouv-delete-${ev.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, highlight }) => (
  <div className={`p-3 rounded-xl border ${highlight ? 'bg-red-500/10 border-red-400/40' : 'bg-white/5 border-white/10'}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-4 h-4 ${highlight ? 'text-red-300' : 'text-white/50'}`} />
      <div className="text-xs text-white/50">{label}</div>
    </div>
    <div className="text-xl font-bold">{value}</div>
  </div>
);

export default AdminMyMouvPanel;